/**
 * 拼图游戏入口。
 *
 * 图源：➕ 本地上传（选文件 / 拖拽）+ 🎲 随机图（Lorem Picsum，免 key）。
 * 玩法：拖拽碎片到画板；接近正确位（且旋转回正）自动吸附；全部吸附即完成。
 * 旋转：右键 / 双击 / R 键。撤销：Ctrl/Cmd+Z。
 *
 * 难度按"简单/中/难/专家"四档，根据图片宽高比智能分割（每块尽量接近正方形）。
 * URL 参数：?diff=easy|medium|hard|expert&rotate=0|1&ref=0|1&image=picsum-<seed>&theme=cozy|dark|light
 */
import { Game } from './game/Game';
import { prepareImage } from './game/imageWorkerClient';
import { createToolbar } from './ui/toolbar';
import { createSettings } from './ui/settings';
import { injectStyles } from './ui/styles';
import { showToast } from './ui/toast';
import { loadImageFromUrl } from './game/imagePipeline';
import { createCanvas, get2D } from './render/canvasUtil';
import type { DifficultyLevel } from './game/grid';

interface StartOpts {
  difficulty: DifficultyLevel;
  rotateMode: boolean;
}

const LEVELS: DifficultyLevel[] = ['easy', 'medium', 'hard', 'expert'];

function parseQuery(): StartOpts & { ref: boolean; image: string | null; theme: string | null } {
  const p = new URLSearchParams(location.search);
  const d = p.get('diff');
  const difficulty: DifficultyLevel = (LEVELS as string[]).includes(d ?? '')
    ? (d as DifficultyLevel)
    : 'medium';
  return {
    difficulty,
    rotateMode: p.get('rotate') === null ? true : p.get('rotate') !== '0',
    ref: p.get('ref') === '1',
    image: p.get('image'),
    theme: p.get('theme'),
  };
}

async function main(): Promise<void> {
  injectStyles();

  const app = document.getElementById('app');
  if (!app) throw new Error('找不到 #app 容器');

  const canvas = createCanvas(window.innerWidth, window.innerHeight);
  canvas.className = 'pintu-canvas';
  canvas.style.touchAction = 'none';
  app.appendChild(canvas);
  const ctx = get2D(canvas);

  const game = new Game(canvas, ctx);
  app.appendChild(game.overlayEl);
  app.appendChild(game.previewEl);
  const settings = createSettings(game, game.store);
  app.appendChild(settings.el);
  app.appendChild(createToolbar(game, game.store, () => settings.open()));

  // —— 底部控件：仅 上传 ➕ + 随机 🎲 ——
  const galleryEl = document.createElement('div');
  galleryEl.className = 'pintu-gallery';

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.hidden = true;

  const uploadBtn = document.createElement('button');
  uploadBtn.type = 'button';
  uploadBtn.className = 'thumb upload';
  uploadBtn.title = '上传图片';
  uploadBtn.textContent = '＋';
  uploadBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) void loadFromFile(f);
    fileInput.value = '';
  });

  const randomBtn = document.createElement('button');
  randomBtn.type = 'button';
  randomBtn.className = 'thumb upload';
  randomBtn.title = '随机换图（来自 Lorem Picsum）';
  randomBtn.textContent = '🎲';
  randomBtn.addEventListener('click', () => void loadRandom());

  galleryEl.appendChild(uploadBtn);
  galleryEl.appendChild(randomBtn);
  app.appendChild(galleryEl);
  app.appendChild(fileInput);

  let loadToken = 0;

  function currentOpts(): StartOpts {
    const c = game.store.get().config;
    return { difficulty: c.difficulty, rotateMode: c.rotateMode };
  }

  async function startWith(file: File, imageKey: string, opts: StartOpts): Promise<void> {
    const myToken = ++loadToken;
    const prepared = await prepareImage(file, 1600);
    if (myToken !== loadToken) return; // 已被更新的请求取代
    game.start(prepared, {
      difficulty: opts.difficulty,
      rotateMode: opts.rotateMode,
      imageKey,
    });
  }

  async function startWithUrl(url: string, key: string, opts?: StartOpts): Promise<void> {
    const myToken = ++loadToken;
    const img = await loadImageFromUrl(url);
    const prepared = await prepareImage(img, 1600);
    if (myToken !== loadToken) return;
    const cfg = opts ?? currentOpts();
    game.start(prepared, {
      difficulty: cfg.difficulty,
      rotateMode: cfg.rotateMode,
      imageKey: key,
    });
  }

  async function loadFromFile(file: File): Promise<void> {
    try {
      await startWith(file, `upload-${file.name}`, currentOpts());
      showToast(`已加载：${file.name}`);
    } catch {
      showToast('无法加载该图片（可能已损坏或格式不支持）');
    }
  }

  async function loadRandom(seed?: number, opts?: StartOpts): Promise<void> {
    const s = seed ?? Math.floor(Math.random() * 1_000_000);
    try {
      await startWithUrl(`https://picsum.photos/seed/${s}/1600/1000`, `picsum-${s}`, opts);
      showToast('已随机加载新图');
    } catch {
      showToast('随机图加载失败，请检查网络');
    }
  }

  // 拖拽图片到页面
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('drop', (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) void loadFromFile(f);
  });

  // 撤销快捷键
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      game.undo();
    }
  });

  // —— 初始：URL image（picsum-<seed>）可复现，否则随机 ——
  const q = parseQuery();
  let initSeed: number | undefined;
  if (q.image && q.image.startsWith('picsum-')) {
    const n = Number(q.image.slice(6));
    if (Number.isFinite(n)) initSeed = n;
  }
  await loadRandom(initSeed, { difficulty: q.difficulty, rotateMode: q.rotateMode });
  if (q.ref) game.toggleReference(true);
  if (q.theme === 'dark' || q.theme === 'light' || q.theme === 'cozy') {
    if (q.theme !== 'cozy') game.setTheme(q.theme);
  }

  // dev 模式暴露，便于 e2e
  if (import.meta.env.DEV) {
    (window as unknown as { __pintu?: unknown }).__pintu = { game, store: game.store };
  }

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => game.resize(), 150);
  });
  document.addEventListener('visibilitychange', () => {
    game.store.mutate((s) => {
      s.paused = document.hidden;
    });
  });
}

void main();
